import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const AddBank = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    swift_code: '',
    iban_prefix: '',
    contact_person: '',
    phone: '',
    email: '',
    address: '',
    website: '',
    logo: null
  });

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          formDataToSend.append(key, formData[key]);
        }
      });

      await axios.post('/api/accounting/banks', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      navigate('/banks');
    } catch (err) {
      setError(err.response?.data?.message || 'حدث خطأ في إضافة البنك');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bank-form-container">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h2>{t('accounting.banks.addBank')}</h2>
          <p>إضافة بنك جديد للنظام</p>
        </div>
        <div className="header-actions">
          <button 
            className="back-btn"
            onClick={() => navigate('/banks')}
          >
            <i className="fas fa-arrow-right"></i>
            {t('accounting.banks.actions.back')}
          </button>
        </div>
      </div>

      <div className="form-container">
        {/* Help Panels */}
        <div className="help-panels">
          <div className="help-panel">
            <h4>{t('accounting.banks.help.title')}</h4>
            <p>{t('accounting.banks.help.description')}</p>
            <ul>
              <li>{t('accounting.banks.help.bankInfo')}</li>
              <li>{t('accounting.banks.help.accountInfo')}</li>
              <li>{t('accounting.banks.help.statusInfo')}</li>
            </ul>
          </div>
        </div>

        {/* Form Section */}
        <div className="form-section">
          <div className="form-card">
            <form onSubmit={handleSubmit} className="bank-form">
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label required">
                    {t('accounting.banks.bankName')}
                  </label>
                  <div className="form-note">
                    اسم البنك كما يظهر في الوثائق الرسمية
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    placeholder="مثال: البنك الأهلي السعودي"
                    required
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.swiftCode')}
                  </label>
                  <div className="form-note">
                    رمز السويفت الدولي للبنك (اختياري)
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="swift_code"
                    value={formData.swift_code}
                    onChange={handleInputChange}
                    placeholder="مثال: NCBKSAJE"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.ibanPrefix')}
                  </label>
                  <div className="form-note">
                    البادئة المستخدمة في أرقام الآيبان (اختياري)
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="iban_prefix"
                    value={formData.iban_prefix}
                    onChange={handleInputChange}
                    placeholder="مثال: SA"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.contactPerson')}
                  </label>
                  <div className="form-note">
                    اسم الشخص المسؤول عن التواصل مع البنك
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    name="contact_person"
                    value={formData.contact_person}
                    onChange={handleInputChange}
                    placeholder="اسم الشخص المسؤول"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.phone')}
                  </label>
                  <div className="form-note">
                    رقم الهاتف للتواصل مع البنك
                  </div>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+966 11 123 4567"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.email')}
                  </label>
                  <div className="form-note">
                    البريد الإلكتروني للتواصل مع البنك
                  </div>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="info@bank.com"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-12">
                  <label className="form-label">
                    {t('accounting.banks.address')}
                  </label>
                  <div className="form-note">
                    العنوان الكامل للبنك
                  </div>
                  <textarea
                    className="form-control"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="العنوان الكامل للبنك"
                    rows="3"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.website')}
                  </label>
                  <div className="form-note">
                    الموقع الإلكتروني للبنك
                  </div>
                  <input
                    type="url"
                    className="form-control"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://www.bank.com"
                  />
                </div>

                <div className="form-group col-md-6">
                  <label className="form-label">
                    {t('accounting.banks.logo')}
                  </label>
                  <div className="form-note">
                    شعار البنك (اختياري)
                  </div>
                  <input
                    type="file"
                    className="form-control"
                    name="logo"
                    onChange={handleInputChange}
                    accept="image/*"
                  />
                </div>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => navigate('/banks')}
                  disabled={loading}
                >
                  {t('accounting.banks.actions.back')}
                </button>
                <button
                  type="submit"
                  className="btn-save"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      جاري الحفظ...
                    </>
                  ) : (
                    t('accounting.banks.actions.save')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddBank;
